import React from "react"
import styled, { css } from "styled-components"
import colors from "@constants/colors"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import { body16MainStyles } from "@constants/typography"
import Backdrop from "@components/Backdrop"
import Header from "./Header"
import Body from "./Body"
import Footer from "./Footer"
import DiscountButton from "./DiscountButton"
import EditCartItem from "./EditCartItem"
import AddDiscount from "./AddDiscount"
import EditDiscount from "./EditDiscount"
import EmptyCart from "./EmptyCart"
import { RecentItemProps } from "./RecentItem"
import ProductSelector from "@components/ProductSelector"
import useEnhancedEcommerceEvents from "@hooks/useEnhancedEcommerceEvents"
import useElevarEvents, { ElevarProductProps } from "@hooks/useElevarEvents"
import useModalBackgroundClose from "@hooks/useModalBackgroundClose"
import { INVENTORY_ITEM_TRACKED } from "@hooks/useMultiWarehouseInventory"
import { DiscountResponseProps } from "./types"
import {
  CartItem,
  AddToCartPayload,
  AutomaticDiscountData,
  AutomaticDiscountAllocations,
  DiscountedProductsData,
} from "@redux/types"
import { getIn } from "@utils/index"
import getAvailabilityByRegion from "@utils/getAvailabilityByRegion"
import { formatCurrency } from "@utils/numbers"
import { getLocalStorage } from "@utils/storage"
import { SELECTED_PRODUCTS_KEY } from "@constants/storageKeys"
import _ from "lodash-es"

const openProps = css`
  opacity: 1;
  transform: translateX(0);
  transition: opacity ${animation.quick} ${cubicBezier},
    transform ${animation.medium} ${cubicBezier};
`

const disableClicksProps = css`
  a,
  button {
    pointer-events: none;
  }
`

const Container = styled.div<{ isOpen: boolean; disableClicks: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  overflow-x: auto;
  width: 100%;
  padding-top: 73px;
  opacity: 0;
  transform: translateX(100%);
  transition: opacity ${animation.medium} ${cubicBezier},
    transform ${animation.quick} ${cubicBezier};
  will-change: opacity, transform;
  background-color: ${colors.offWhite};
  cursor: default;

  ${({ isOpen }) => (isOpen ? openProps : "")}
  ${({ disableClicks }) => (disableClicks ? disableClicksProps : "")}

  @media ${breakpoints.laptop} {
    max-width: 1088px;
    padding-top: 115px;
  }
`

const Content = styled.div`
  overflow: hidden;
  overflow-y: auto;
  height: 100%;

  @media ${breakpoints.laptop} {
    align-items: flex-start;
    justify-content: flex-start;
    display: flex;
  }
`

const Row = styled.div`
  align-items: center;
  justify-content: space-between;
  display: flex;
  height: 56px;
  padding: 0 20px;

  @media ${breakpoints.laptop} {
    display: none;
  }
`

const Text = styled.div`
  ${body16MainStyles}
  color: ${colors.mediumDarkGray};
`

export interface CartProps {
  isOpen: boolean
  items: Array<CartItem>
  subtotal: string
  total: string
  cartCount: number
  checkoutLink?: string
  showPromoToast: boolean
  recentItems: Array<RecentItemProps>
  couponCodeApplied?: string
  automaticDiscounts: AutomaticDiscountData[]
  automaticDiscountData: any
  automaticDiscountApplied: boolean
  automaticDiscountAllocations: AutomaticDiscountAllocations
  discountedProductsData: DiscountedProductsData
  discountedProductsInventoryData: any
  customerLocation: any
  addToCart: (payload: AddToCartPayload) => void
  onUpdateCartItem: ({
    cartItem,
    lineIndex,
  }: {
    cartItem: CartItem
    lineIndex: number
  }) => void
  onUpdateDiscount: (discount: string | undefined) => DiscountResponseProps
  onRemoveDiscount: () => void
  onRequestClose: () => void
}

const Cart: React.FC<CartProps> = ({
  isOpen,
  items,
  subtotal,
  total,
  cartCount,
  checkoutLink,
  showPromoToast,
  recentItems,
  couponCodeApplied,
  automaticDiscounts,
  automaticDiscountData,
  automaticDiscountApplied,
  automaticDiscountAllocations,
  discountedProductsData,
  discountedProductsInventoryData,
  customerLocation,
  addToCart,
  onUpdateCartItem,
  onUpdateDiscount,
  onRemoveDiscount,
  onRequestClose,
}) => {
  const cartRef: any = React.useRef(null)
  const addDiscountRef: any = React.useRef(null)
  const editDiscountRef: any = React.useRef(null)
  const editCartItemRef: any = React.useRef(null)
  const productSelectorRef: any = React.useRef(null)

  const [delta, setDelta] = React.useState<number>(0)
  const [cartItemToUpdate, setCartItemToUpdate] = React.useState<
    { cartItem: CartItem; lineIndex: number } | undefined
  >(undefined)

  const { onRemoveProductFromCart } = useEnhancedEcommerceEvents()
  const { elevarOnAddToCart, elevarOnRemoveFromCart, elevarOnViewCart } =
    useElevarEvents()

  React.useEffect(() => {
    if (isOpen) {
      elevarOnViewCart(
        items.map((item, i) => ({
          sku: item.sku,
          productId: item.shopifyProductId,
          variantId: item.shopifyVariantId,
          productName: item.productName,
          variantName: item.variantName,
          price: item.price,
          brand: "Urban Armor Gear",
          device: item.device,
          deviceType: item.deviceType,
          quantity: String(item.quantity),
          position: String(i + 1),
        })) as Array<ElevarProductProps>,
        total
      )
    }
  }, [isOpen])

  const onRemoveCartItem = (cartItem: CartItem, lineIndex: number): void => {
    onUpdateCartItem({
      cartItem: {
        ...cartItem,
        quantity: 0,
      },
      lineIndex,
    })

    onRemoveProductFromCart({
      productId: cartItem.shopifyProductId,
      productName: cartItem.productName,
      variantName: cartItem.variantName,
      sku: cartItem.sku,
      price: cartItem.price,
      brand: cartItem.brand,
      device: cartItem.device,
      deviceType: cartItem.deviceType,
      quantity: 0,
    })

    const selectedProducts = getLocalStorage(SELECTED_PRODUCTS_KEY) || {}
    const list = selectedProducts[cartItem.shopifyProductId] || ""

    elevarOnRemoveFromCart(
      {
        sku: cartItem.sku,
        productId: cartItem.shopifyProductId,
        variantId: cartItem.shopifyVariantId,
        productName: cartItem.productName,
        variantName: cartItem.variantName,
        image: cartItem.image,
        price: cartItem.price,
        brand: "Urban Armor Gear",
        device: cartItem.device,
        deviceType: cartItem.deviceType,
        quantity: cartItem.quantity,
        list,
      },
      list
    )
  }

  const handleOnRemoveCartItemMobile = () => {
    if (cartItemToUpdate) {
      onRemoveCartItem(cartItemToUpdate.cartItem, cartItemToUpdate.lineIndex)
    }
    setDelta(0)
    setCartItemToUpdate(undefined)
  }

  const handleOnUpdateCartItemMobile = () => {
    if (cartItemToUpdate?.cartItem && delta !== 0) {
      const updatedQuantity = cartItemToUpdate.cartItem.quantity + delta
      const selectedProducts = getLocalStorage(SELECTED_PRODUCTS_KEY) || {}
      const list =
        selectedProducts[cartItemToUpdate.cartItem.shopifyProductId] || ""
      const elevarProduct = {
        sku: cartItemToUpdate.cartItem.sku,
        productId: cartItemToUpdate.cartItem.shopifyProductId,
        variantId: cartItemToUpdate.cartItem.shopifyVariantId,
        productName: cartItemToUpdate.cartItem.productName,
        variantName: cartItemToUpdate.cartItem.variantName,
        image: cartItemToUpdate.cartItem.image,
        price: cartItemToUpdate.cartItem.price,
        brand: "Urban Armor Gear",
        device: cartItemToUpdate.cartItem.device,
        deviceType: cartItemToUpdate.cartItem.deviceType,
        quantity: Math.abs(delta),
        list,
      }

      if (delta > 0) {
        elevarOnAddToCart(elevarProduct, list)
      } else {
        elevarOnRemoveFromCart(elevarProduct, list)
      }

      onUpdateCartItem({
        cartItem: { ...cartItemToUpdate.cartItem, quantity: updatedQuantity },
        lineIndex: cartItemToUpdate.lineIndex,
      })
    }
    setDelta(0)
    setCartItemToUpdate(undefined)
  }

  const [showAddDiscount, setShowAddDiscount] = React.useState<boolean>(false)

  const openAddDiscount = () => {
    setShowAddDiscount(true)
  }

  const closeAddDiscount = () => {
    setShowAddDiscount(false)
  }

  const onAddDiscountSuccess = () => {
    closeAddDiscount()
  }

  const [showEditDiscount, setShowEditDiscount] = React.useState<boolean>(false)

  const openEditDiscount = () => {
    setShowEditDiscount(true)
  }

  const closeEditDiscount = () => {
    setShowEditDiscount(false)
  }

  const handleOnRemoveDiscount = () => {
    closeEditDiscount()
    onRemoveDiscount()
  }

  useModalBackgroundClose({
    modalRef: cartRef,
    excludeRefs: [
      addDiscountRef,
      editDiscountRef,
      editCartItemRef,
      productSelectorRef,
    ],
    isOpen,
    onRequestClose,
  })

  const [productSelectorProducts, setProductSelectorProducts] =
    React.useState<any>([])
  const [productSelectorDiscountConfig, setProductSelectorDiscountConfig] =
    React.useState<any>({})

  const closeProductSelector = () => {
    setProductSelectorProducts([])
    setProductSelectorDiscountConfig({})
  }

  const { customerCountryCode } = customerLocation

  const availableSkusByRegion = React.useMemo(() => {
    const discountVariants = _.flatten(
      Object.values(discountedProductsData).map(({ variants }) => variants)
    )
    const availableSkus: any = []

    discountVariants.forEach(({ sku }) => {
      const tracked = getIn(
        discountedProductsInventoryData,
        `${sku}.${INVENTORY_ITEM_TRACKED}`,
        true
      )

      if (
        tracked &&
        getAvailabilityByRegion(
          customerCountryCode,
          sku,
          discountedProductsInventoryData
        ).isAvailable
      ) {
        availableSkus.push(sku)
      }
    })

    return availableSkus
  }, [
    discountedProductsInventoryData,
    discountedProductsData,
    customerLocation,
  ])

  const formattedItems = React.useMemo(() => {
    return items.map((item, i) => {
      const allDiscountProductsIndexIdMap = {}
      const allDiscountProducts: any = []
      const allDiscountBlockProps: any = []
      let decodedShopifyProductId

      try {
        decodedShopifyProductId = atob(item.shopifyProductId || "")
      } catch (error) {
        decodedShopifyProductId = item.shopifyProductId
      }

      automaticDiscounts.forEach((automaticDiscount, i) => {
        const discountProducts: any = []
        let showDiscountBlock = false
        let discountType

        if (
          automaticDiscount.customerBuysProducts &&
          automaticDiscount.customerBuysProducts.indexOf(
            decodedShopifyProductId
          ) > -1
        ) {
          discountType = "X"
          automaticDiscount.customerGetsProducts.forEach((id) => {
            const contentfulId = automaticDiscount.idMap[id]

            if (discountedProductsData[contentfulId]) {
              discountProducts.push(discountedProductsData[contentfulId])
            }
          })

          let counterpartItemCount = 0

          discountProducts.forEach((dp) => {
            items
              .filter(
                ({ contentfulProductId }) =>
                  contentfulProductId === dp.contentful_id
              )
              .forEach((item) => {
                counterpartItemCount += item.quantity
              })
          })

          if (item.quantity > counterpartItemCount) {
            showDiscountBlock = true
          }
        } else if (
          automaticDiscount.customerGetsProducts &&
          automaticDiscount.customerGetsProducts.indexOf(
            decodedShopifyProductId
          ) > -1
        ) {
          discountType = "Y"
          automaticDiscount.customerBuysProducts.forEach((id) => {
            const contentfulId = automaticDiscount.idMap[id]

            if (discountedProductsData[contentfulId]) {
              discountProducts.push(discountedProductsData[contentfulId])
            }
          })

          let counterpartItemCount = 0

          discountProducts.forEach((dp) => {
            items
              .filter(
                ({ contentfulProductId }) =>
                  contentfulProductId === dp.contentful_id
              )
              .forEach((item) => {
                counterpartItemCount += item.quantity
              })
          })

          if (item.quantity > counterpartItemCount) {
            showDiscountBlock = true
          }
        }

        allDiscountProducts.push(discountProducts)
        allDiscountBlockProps.push({
          showDiscountBlock,
          discountType,
        })
        allDiscountProductsIndexIdMap[i] = automaticDiscount.id
      })

      allDiscountProducts.forEach((products, i) => {
        const discountId = allDiscountProductsIndexIdMap[i]
        const parsedDiscountId = discountId.replace(
          "gid://shopify/DiscountAutomaticNode/",
          ""
        )
        const discountConfig = automaticDiscountData?.find(
          ({ discountId }) => discountId === parsedDiscountId
        )

        if (
          discountConfig &&
          products.length > 0 &&
          allDiscountBlockProps[i] &&
          allDiscountBlockProps[i].discountType
        ) {
          allDiscountBlockProps[i].discountBlockCallout =
            discountConfig[
              `pdpMessage${allDiscountBlockProps[i].discountType}`
            ] || ""
          allDiscountBlockProps[i].discountBlockTitle =
            products.length === 1
              ? products[0].productName
              : allDiscountBlockProps[i].discountType === "X"
              ? discountConfig.selectXMessage
              : allDiscountBlockProps[i].discountType === "Y"
              ? discountConfig.selectYMessage
              : ""
          allDiscountBlockProps[i].discountImage =
            allDiscountBlockProps[i].discountType === "X"
              ? discountConfig.selectYImage
              : allDiscountBlockProps[i].discountType === "Y"
              ? discountConfig.selectXImage
              : ""
        }
      })

      const allFilteredDiscountProducts: any = []

      allDiscountProducts.forEach((discountProducts, i) => {
        let filteredDiscountProducts = discountProducts.filter(
          ({ variants }) => {
            return variants && variants.length > 0
          }
        )
        const discountId = allDiscountProductsIndexIdMap[i]
        const parsedDiscountId = discountId.replace(
          "gid://shopify/DiscountAutomaticNode/",
          ""
        )
        const discountConfig = automaticDiscountData?.find(
          ({ discountId }) => discountId === parsedDiscountId
        )

        if (!discountConfig) {
          return
        } else if (item.device === "Portable Charger") {
          filteredDiscountProducts = filteredDiscountProducts.filter((p) => {
            return !!p.builtInMagnet
          })
        } else if (discountConfig.matchDevice) {
          const hasMagnet = item.builtInMagnet

          filteredDiscountProducts = filteredDiscountProducts.filter((p) => {
            return hasMagnet
              ? p.device.name === item.device ||
                  p.device.name === "Portable Charger"
              : p.device.name === item.device
          })
        } else if (
          item?.productSubType &&
          ["cases", "screen-protectors"].indexOf(item?.productSubType) > -1
        ) {
          filteredDiscountProducts = filteredDiscountProducts.filter((p) => {
            return p.productSubType &&
              ["cases", "screen-protectors"].indexOf(p.productSubType.slug) > -1
              ? p.device.name === item.device
              : true
          })
        }

        allFilteredDiscountProducts.push(
          filteredDiscountProducts.map((p) => {
            const imageSrc = _.get(p, "variants[0].images[0].file.url", "")
            const [cleanedSrc] = imageSrc.split("?")
            const productImage = `https:${cleanedSrc}`
            const productPrice = _.get(p, "p.variants[0].price", "")
            const productCompareAtPrice = _.get(p, "variants[0].compareAtPrice")
            const marketPrices = _.get(p, "variants[0].marketPrices", {})

            return {
              ...p,
              productImage,
              productPrice,
              productCompareAtPrice,
              marketPrices,
            }
          })
        )
      })

      return {
        item,
        lineIndex: i,
        automaticDiscountAllocations,
        discountProducts: allDiscountProducts,
        discountBlockProps: allDiscountBlockProps,
        availableSkus: availableSkusByRegion,
        onClickEdit: (e) => {
          e.stopPropagation()
          setCartItemToUpdate({ cartItem: item, lineIndex: i })
        },
        onUpdateCartItem,
        onRemoveCartItem,
        openProductSelector: allFilteredDiscountProducts.map(
          (discountProducts, i) => {
            if (discountProducts.length > 0) {
              return () => {
                const discountId = allDiscountProductsIndexIdMap[i]
                const parsedDiscountId = discountId.replace(
                  "gid://shopify/DiscountAutomaticNode/",
                  ""
                )
                const discountConfig = automaticDiscountData?.find(
                  ({ discountId }) => discountId === parsedDiscountId
                )

                setProductSelectorProducts(discountProducts)
                setProductSelectorDiscountConfig(discountConfig)
              }
            }

            return () => {}
          }
        ),
        addDiscountedProductToCart: addToCart,
      }
    })
  }, [items, automaticDiscounts, discountedProductsData])

  return (
    <Backdrop isOpen={isOpen}>
      <Container
        ref={cartRef}
        isOpen={isOpen}
        disableClicks={showAddDiscount || showEditDiscount}
      >
        <Header cartCount={cartCount} onRequestClose={onRequestClose} />
        {items.length ? (
          <Content>
            <Body items={formattedItems} />
            {/* CARTODO */}
            {/* {parseFloat(subtotal) !== parseFloat(total) && (
              <Row>
                <Text>Discount</Text>
                <Text>
                  {`- `}
                  {formatCurrency(
                    parseFloat(subtotal) - parseFloat(total),
                    "USD",
                    "en-US"
                  )}
                </Text>
              </Row>
            )} */}
            <DiscountButton
              discount={couponCodeApplied}
              openAddDiscount={openAddDiscount}
              openEditDiscount={openEditDiscount}
            />
            <Footer
              discount={couponCodeApplied}
              onAddDiscount={onUpdateDiscount}
              onAddDiscountSuccess={onAddDiscountSuccess}
              onRemoveDiscount={handleOnRemoveDiscount}
              subtotal={subtotal}
              total={total}
              checkoutLink={checkoutLink}
              showPromoToast={showPromoToast}
            />
          </Content>
        ) : (
          <EmptyCart recentItems={recentItems} />
        )}
      </Container>
      <EditCartItem
        containerRef={editCartItemRef}
        isOpen={!!cartItemToUpdate}
        product={cartItemToUpdate}
        delta={delta}
        setDelta={setDelta}
        onRemoveCartItem={handleOnRemoveCartItemMobile}
        onUpdateCartItem={handleOnUpdateCartItemMobile}
      />
      <AddDiscount
        containerRef={addDiscountRef}
        isOpen={showAddDiscount}
        onAddDiscount={onUpdateDiscount}
        onAddDiscountSuccess={onAddDiscountSuccess}
        onRequestClose={closeAddDiscount}
      />
      <EditDiscount
        containerRef={editDiscountRef}
        isOpen={showEditDiscount}
        discount={couponCodeApplied}
        onRemoveDiscount={handleOnRemoveDiscount}
        onRequestClose={closeEditDiscount}
      />
      <ProductSelector
        containerRef={productSelectorRef}
        isOpen={productSelectorProducts.length > 0}
        header={productSelectorDiscountConfig?.pdpMessage || ""}
        deal={productSelectorDiscountConfig?.pdpMessage || ""}
        products={productSelectorProducts}
        availableVariants={availableSkusByRegion}
        closeOnAddToCart
        showPromoToast={true}
        addToCart={addToCart}
        onRequestClose={closeProductSelector}
      />
    </Backdrop>
  )
}

export default Cart
